import { apiFetch } from "../utils/api";

const GUEST_KEY = "love_map_guest_token";
const PROGRESS_KEY = "love_map_progress_v2";

export function getLoveMapGuestToken() {
  let token = localStorage.getItem(GUEST_KEY);
  if (!token) {
    token =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `lm_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(GUEST_KEY, token);
  }
  return token;
}

export function saveLoveMapProgress(data) {
  localStorage.setItem(PROGRESS_KEY, JSON.stringify(data));
}

export function loadLoveMapProgress() {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearLoveMapProgress() {
  localStorage.removeItem(PROGRESS_KEY);
}

function authBody(extra = {}) {
  const userId = localStorage.getItem("user_id");
  return {
    guest_token: getLoveMapGuestToken(),
    user_id: userId ? Number(userId) : null,
    ...extra,
  };
}

export async function fetchLoveMapMeta() {
  const res = await apiFetch("/api/love-map/meta");
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "加载失败");
  return data;
}

export async function createLoveMapSession(mode = "full") {
  const res = await apiFetch("/api/love-map/session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(authBody({ mode })),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "创建会话失败");
  return data;
}

export async function expandLoveMapToFull(sessionId) {
  const res = await apiFetch("/api/love-map/session/expand-full", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(authBody({ session_id: sessionId })),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "切换完整版失败");
  return data;
}

export async function submitLoveMapAnswer({
  sessionId,
  deck,
  cardId,
  answer,
  index,
}) {
  const res = await apiFetch("/api/love-map/answer", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(
      authBody({
        session_id: sessionId,
        deck,
        card_id: cardId,
        answer,
        index,
      })
    ),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "保存失败");
  return data;
}

export async function completeLoveMapSelf(sessionId) {
  const res = await apiFetch("/api/love-map/complete-self", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(authBody({ session_id: sessionId })),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "提交失败");
  return data;
}

export async function completeLoveMapExpectation(sessionId) {
  const res = await apiFetch("/api/love-map/complete-expectation", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(authBody({ session_id: sessionId })),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "提交失败");
  return data;
}

export async function generateLoveMap(sessionId) {
  const res = await apiFetch("/api/love-map/generate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(authBody({ session_id: sessionId })),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "生成失败");
  return data;
}

export async function fetchLoveMapResult(sessionId) {
  const token = getLoveMapGuestToken();
  const userId = localStorage.getItem("user_id");
  const qs = new URLSearchParams({ guest_token: token });
  if (userId) qs.set("user_id", userId);
  const res = await apiFetch(`/api/love-map/result/${sessionId}?${qs}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "加载结果失败");
  return data;
}

export async function requestLoveMapAiReport(sessionId) {
  const res = await apiFetch("/api/love-map/ai-report", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(authBody({ session_id: sessionId })),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "生成报告失败");
  return data;
}

export async function fetchLoveMapMatches({ refresh = false, sessionId } = {}) {
  const userId = localStorage.getItem("user_id");
  if (!userId) throw new Error("请先登录后再查看匹配");
  const qs = new URLSearchParams({
    user_id: String(userId),
    refresh: refresh ? "1" : "0",
    guest_token: getLoveMapGuestToken(),
  });
  if (sessionId) qs.set("session_id", String(sessionId));
  const res = await apiFetch(`/api/love-map/matches?${qs}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "加载匹配失败");
  return data;
}

export async function fetchLoveMapMatchDetail(targetUserId) {
  const userId = localStorage.getItem("user_id");
  if (!userId) throw new Error("请先登录");
  const res = await apiFetch(
    `/api/love-map/matches/${targetUserId}?user_id=${userId}`
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "加载详情失败");
  return data;
}

function requireUserId() {
  const userId = localStorage.getItem("user_id");
  if (!userId) throw new Error("请先登录");
  return Number(userId);
}

export async function inviteLoveMapCouple(targetUserId, message = "") {
  const userId = requireUserId();
  const res = await apiFetch("/api/love-map/couple/invite", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user_id: userId,
      target_user_id: targetUserId,
      message,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "邀请失败");
  return data;
}

export async function fetchLoveMapCoupleInvites() {
  const userId = requireUserId();
  const res = await apiFetch(`/api/love-map/couple/invites?user_id=${userId}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "加载邀请失败");
  return data;
}

export async function respondLoveMapCoupleInvite(inviteId, action) {
  const userId = requireUserId();
  const res = await apiFetch("/api/love-map/couple/respond", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user_id: userId,
      invite_id: inviteId,
      action,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "操作失败");
  return data;
}

export async function generateLoveMapCouple(partnerUserId) {
  const userId = requireUserId();
  const res = await apiFetch("/api/love-map/couple/generate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user_id: userId,
      partner_user_id: partnerUserId,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "生成失败");
  return data;
}

export async function fetchLoveMapCoupleList() {
  const userId = requireUserId();
  const res = await apiFetch(`/api/love-map/couple/list?user_id=${userId}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "加载失败");
  return data;
}

export async function fetchLoveMapCoupleWith(partnerUserId) {
  const userId = requireUserId();
  const res = await apiFetch(
    `/api/love-map/couple/with/${partnerUserId}?user_id=${userId}`
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "加载失败");
  return data;
}

export async function requestLoveMapCoupleAiReport(coupleMapId) {
  const userId = requireUserId();
  const res = await apiFetch("/api/love-map/couple/ai-report", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user_id: userId,
      couple_map_id: coupleMapId,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "生成报告失败");
  return data;
}
